import React, { useState } from 'react';
import { useField } from 'react-final-form';
import { useLocation, useParams } from 'react-router-dom';
import {
  Button, FormControl, FormErrorMessage, FormLabel, HStack, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select,
  Stack, Text, useDisclosure, useToast,
} from '@chakra-ui/react';
import { NIL } from 'uuid';
import FieldLabel from './FieldLabel';
import { useProjectMode } from '../../theme/ProjectModeProvider';
import useProjects from '../../hooks/useProjects';
import useProject from '../../hooks/useProject';
import useContacts from '../../hooks/useContacts';
import { Project } from '../../models/project';
import { ContactDto } from '../../models/contactInfo';

// The required Project picker shared by every document create/edit form (proposal, work
// order, purchase order, bill). Beyond listing EXISTING projects it offers "➕ New
// project…", which opens a quick-create modal (name + customer) so a document never has
// to be abandoned just to make its project — nothing in Basix may exist without a
// project (Chad, 2026-06-22). The backend requires a real customer to create a project,
// so the modal asks for one; the new project is created, auto-selected, and the dropdown
// refreshed in place (the API returns the new project's id).
const NEW_SENTINEL = '__new_project__';

interface ProjectSelectFieldProps {
  name?: string;
  label?: string;
  isRequired?: boolean;
}

const ProjectSelectField: React.FC<ProjectSelectFieldProps> = ({
  name = 'projectId', label = 'Project', isRequired = true,
}) => {
  const { input, error, touched } = useField(name, { type: 'select' });
  const { data: projects, status: projectsStatus } = useProjects();
  const { data: contacts, status: contactsStatus } = useContacts();
  const { setProject } = useProject({ id: NIL });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [newName, setNewName] = useState('');
  const [newCustomerId, setNewCustomerId] = useState('');
  const [saving, setSaving] = useState(false);
  // keep the just-created project as an option until the list query refetches, so the
  // Select doesn't blank out for the frame between create and the projects refresh
  const [justCreated, setJustCreated] = useState<{ id: string; name: string } | undefined>();

  // item 223/544 — creating a NEW doc FROM a project: the scaffold prefilled projectId from
  // the ?projectId= param the project's grid passed, so lock the picker to it (read-only) —
  // the user came from that project and shouldn't have to re-pick. A DIRECT add (no param)
  // leaves it an editable required dropdown.
  const { id: routeId } = useParams<{ id: string }>();
  const { search } = useLocation();
  const { activeProject } = useProjectMode();
  const paramProjectId = new URLSearchParams(search).get('projectId') || undefined;
  const lockedToProject = routeId === NIL && !!paramProjectId && input.value === paramProjectId;

  const invalid = !!(error && touched);
  const list = projects?.index ?? [];
  const lockedName = list.find((p) => p.id === input.value)?.name ?? activeProject?.name ?? '';
  const customers = (contacts?.index ?? []).filter((c: ContactDto) => !c.isVendor);
  const showJustCreated = !!justCreated && !list.some((p) => p.id === justCreated?.id);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === NEW_SENTINEL) {
      setNewName('');
      setNewCustomerId('');
      onOpen();
      return; // never persist the sentinel as the field value
    }
    input.onChange(e);
  };

  const createProject = async () => {
    if (!newName.trim() || !newCustomerId) return;
    setSaving(true);
    try {
      // only Name + Customer are needed; ProjectId auto-generates when blank, salesRep
      // defaults to none (NIL). The API returns the new project's Guid.
      const newId = await setProject({
        id: '',
        name: newName.trim(),
        customerId: newCustomerId,
        salesRepId: NIL,
        projectId: '',
        serviceName: '',
        requestedBy: '',
        description: '',
      } as unknown as Project) as unknown as string;
      setJustCreated({ id: newId, name: newName.trim() });
      input.onChange(newId); // auto-select the project we just made
      onClose();
      toast({ status: 'success', title: `Project "${newName.trim()}" created` });
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'Could not create project',
        description: err?.response?.data ?? 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormControl isInvalid={invalid} isRequired={isRequired}>
      {/* item 332b — the shared FieldLabel: same reserved 2-line height + legible color
          as every other control (the raw brand.700 FormLabel sat higher than its row
          siblings and washed out on saturated brand chromes — Chad's screenshot) */}
      <FieldLabel htmlFor={name}>{label}</FieldLabel>
      {lockedToProject ? (
        // locked to the project you came from (item 223/544) — read-only, no list
        <Input value={lockedName} isReadOnly isDisabled id={name} />
      ) : (
        <Select {...input} onChange={handleChange} isInvalid={invalid} id={name}>
          <option value="">{label}</option>
          {projectsStatus === 'success' && list.map((p: Project) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
          {showJustCreated && (
            <option key={justCreated?.id} value={justCreated?.id}>{justCreated?.name}</option>
          )}
          <option value={NEW_SENTINEL}>➕ New project…</option>
        </Select>
      )}
      <FormErrorMessage>{error}</FormErrorMessage>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New project</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Project name</FormLabel>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Project name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Customer</FormLabel>
                <Select
                  value={newCustomerId}
                  onChange={(e) => setNewCustomerId(e.target.value)}
                  placeholder="Select a customer"
                >
                  {customers.map((c: ContactDto) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
                {contactsStatus === 'success' && customers.length === 0 && (
                  <Text fontSize="xs" color="orange.400" mt={1}>
                    No customers yet — add a customer first, then create the project.
                  </Text>
                )}
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button
                onClick={createProject}
                isLoading={saving}
                isDisabled={!newName.trim() || !newCustomerId}
              >
                Create &amp; select
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormControl>
  );
};

ProjectSelectField.defaultProps = {
  name: 'projectId',
  label: 'Project',
  isRequired: true,
};

export default ProjectSelectField;
